import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { crmSettingsApi } from "../../api/crmSettings";
import { useTenantBranding } from "../../../../shared/branding/TenantBrandingProvider";
import defaultLogo from "../../../../images/logo2.png";
import styles from "./CRMSettingsPage.module.css";
import SourcesPage from "../../../sales/pages/sourcesPage/SourcesPage";
import PipelinePage from "../../../sales/pages/pipeline/PipelinePage";
import ContactCustomFieldsTab from "./tabs/ContactCustomFieldsTab";
import ProductCustomFieldsTab from "./tabs/ProductCustomFieldsTab";
import ResponsiveSettingsTabs from "../../components/ResponsiveSettingsTabs";

import {
  getUserRole,
  getUserPermissions,
} from "../../../../shared/lib/auth/authHelper";

import BrandingTab from "./tabs/BrandingTab";
import EmailTab from "./tabs/EmailTab";
import AITab from "./tabs/AITab";
import GoogleTab from "./tabs/GoogleTab";
import RateLimitTab from "./tabs/RateLimitTab";
import ExternalApiTab from "./tabs/ExternalApiTab";
import WorkScheduleTab from "./tabs/WorkScheduleTab";
import ModulesTab from "./tabs/ModulesTab";

const TABS = [
  { key: "modules", labelKey: "crm_settings_tab_modules" },
  { key: "branding", labelKey: "crm_settings_tab_branding" },
  { key: "email", labelKey: "crm_settings_tab_email" },
  { key: "ai", labelKey: "crm_settings_tab_ai" },
  { key: "google", labelKey: "crm_settings_tab_google" },
  { key: "rateLimit", labelKey: "crm_settings_tab_rate_limit" },
  { key: "workSchedule", labelKey: "crm_settings_tab_work_schedule" },
  { key: "files", labelKey: "crm_settings_tab_files_photos" },
  // { key: "leads", labelKey: "crm_settings_tab_leads" },
  { key: "sources", labelKey: "crm_settings_tab_sources" },
  { key: "pipeline", labelKey: "sales_pipeline" },
  { key: "contactCustomFields", labelKey: "crm_settings_tab_contact_fields" },
  { key: "productCustomFields", labelKey: "crm_settings_tab_product_fields" },
  // { key: "deals", labelKey: "crm_settings_tab_deals" },
  // { key: "notifications", labelKey: "crm_settings_tab_notifications" },
  { key: "externalApi", labelKey: "crm_settings_tab_external_api" },
];

function normalizeRoleName(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function normalizePermissionName(permission) {
  return String(permission || "").trim().toLowerCase();
}

export default function CRMSettingsPage() {
  const { t } = useTranslation();

  const { refresh: refreshBranding } = useTenantBranding();

  const rawRoleName = useMemo(() => getUserRole(), []);
  const roleName = useMemo(() => normalizeRoleName(rawRoleName), [rawRoleName]);

  const permissions = useMemo(() => {
    const raw = getUserPermissions();
    return Array.isArray(raw) ? raw.map(normalizePermissionName) : [];
  }, []);

  const isSuperAdmin = useMemo(() => roleName === "superadmin", [roleName]);
  const isAdmin = useMemo(
    () => roleName === "superadmin" || roleName === "admin",
    [roleName]
  );

  // const hasRateLimitsViewPermission = useMemo(
  //   () => permissions.includes("system.rate_limits.view"),
  //   [permissions]
  // );

  // const hasRateLimitsUpdatePermission = useMemo(
  //   () => permissions.includes("system.rate_limits.update"),
  //   [permissions]
  // );

  const [activeTab, setActiveTab] = useState("branding");

  const [loading, setLoading] = useState(true);
  const [academyEnabled, setAcademyEnabled] = useState(true);
  const [retailShopEnabled, setRetailShopEnabled] = useState(true);
  const [retailShopModules, setRetailShopModules] = useState({ shop: true, auction: false, marketplace: false });

  const [companyName, setCompanyName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#4f46e5");
  const [loginButtonColor, setLoginButtonColor] = useState("#2575fc");

  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);

  const [defaultLeadSource, setDefaultLeadSource] = useState("Website");
  const [leadAutoAssign, setLeadAutoAssign] = useState(true);

  const [defaultDealStage, setDefaultDealStage] = useState("new");
  const [currency, setCurrency] = useState("EUR");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [taskReminders, setTaskReminders] = useState(true);

  const [workScheduleEnabled, setWorkScheduleEnabled] = useState(false);
  const [workScheduleMode, setWorkScheduleMode] = useState("recurring");
  const [workScheduleSlotGeneration, setWorkScheduleSlotGeneration] = useState("duration");
  const [workScheduleAppointmentCount, setWorkScheduleAppointmentCount] = useState(2);
  const [workScheduleStartTime, setWorkScheduleStartTime] = useState("09:00");
  const [workScheduleEndTime, setWorkScheduleEndTime] = useState("18:00");
  const [workScheduleSlotMinutes, setWorkScheduleSlotMinutes] = useState(15);
  const [workScheduleMinAheadMinutes, setWorkScheduleMinAheadMinutes] = useState(15);
  const [filesWorkspaceLayout, setFilesWorkspaceLayout] = useState("gallery");

  const [emailMode, setEmailMode] = useState("hybrid");
  const [replyToEmployee, setReplyToEmployee] = useState(true);

  const [corporateFromEmail, setCorporateFromEmail] = useState("");
  const [corporateFromName, setCorporateFromName] = useState("");

  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpTls, setSmtpTls] = useState(true);

  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [imapUser, setImapUser] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  const [imapSsl, setImapSsl] = useState(true);
  const [emailInnerTab, setEmailInnerTab] = useState("smtp");

  const [aiMode, setAiMode] = useState("basic");
  const [aiFallbackEnabled, setAiFallbackEnabled] = useState(true);
  const [aiLogOutputs, setAiLogOutputs] = useState(false);

  const [aiContactSummaryEnabled, setAiContactSummaryEnabled] = useState(true);
  const [aiEmailWriterEnabled, setAiEmailWriterEnabled] = useState(false);
  const [aiLeadScoringEnabled, setAiLeadScoringEnabled] = useState(false);
  const [aiNextStepEnabled, setAiNextStepEnabled] = useState(true);

  const [aiOpenaiApiKey, setAiOpenaiApiKey] = useState("");
  const [aiOpenaiModel, setAiOpenaiModel] = useState("gpt-4o-mini");
  const [aiOpenaiTemperature, setAiOpenaiTemperature] = useState(0.3);
  const [aiOpenaiMaxTokens, setAiOpenaiMaxTokens] = useState(500);

  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");
  const [googleRedirectUri, setGoogleRedirectUri] = useState("");
  const [googleFrontendSuccessUrl, setGoogleFrontendSuccessUrl] = useState("");
  const [googleFrontendErrorUrl, setGoogleFrontendErrorUrl] = useState("");
  const [googleDriveStatus, setGoogleDriveStatus] = useState(null);
  const [googleDriveLoading, setGoogleDriveLoading] = useState(false);
  const [allowPublicEmployeeRegistration, setAllowPublicEmployeeRegistration] =
    useState(true);
  const [verificationCodeTtlMinutes, setVerificationCodeTtlMinutes] =
    useState(3);

  const baselineRef = useRef(null);
  const settingsJsonRef = useRef({});

  const previewLogoUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile]
  );
  const previewFaviconUrl = useMemo(
    () => (faviconFile ? URL.createObjectURL(faviconFile) : null),
    [faviconFile]
  );

  useEffect(() => {
    return () => {
      if (previewLogoUrl) URL.revokeObjectURL(previewLogoUrl);
      if (previewFaviconUrl) URL.revokeObjectURL(previewFaviconUrl);
    };
  }, [previewLogoUrl, previewFaviconUrl]);

  useEffect(() => {
    if (
      (activeTab === "sources" ||
        activeTab === "pipeline" ||
        activeTab === "contactCustomFields" ||
        activeTab === "productCustomFields") &&
      !isAdmin
    ) {
      setActiveTab("branding");
    }
  }, [activeTab, isAdmin]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await crmSettingsApi.get();
        crmSettingsApi.getGoogleDriveStatus().then(setGoogleDriveStatus).catch(() => setGoogleDriveStatus(null));
        if (!mounted) return;

        settingsJsonRef.current = data?.settings_json && typeof data.settings_json === "object" ? data.settings_json : {};

        setCompanyName(data.company_name || "");
        setPrimaryColor(data.primary_color || "#4f46e5");

        const fromJson =
          data?.settings_json?.branding?.login_button_color ||
          data?.settings_json?.login_button_color ||
          null;

        setLoginButtonColor(fromJson || data.login_button_color || "#2575fc");

        setDefaultLeadSource(data.default_lead_source || "Website");
        setDefaultDealStage(data.default_deal_stage || "new");
        setCurrency(data.currency || "EUR");
        setEmailNotifications(!!data.email_notifications);
        setTaskReminders(!!data.task_reminders);

        setLogoUrl(data.logo_url || null);
        setFaviconUrl(data.favicon_url || null);

        const email = data?.settings_json?.email || {};
        const corp = email?.corporate || {};
        const smtp = corp?.smtp || {};
        const imap = corp?.imap || {};

        setEmailMode(email?.mode || "hybrid");
        setReplyToEmployee(email?.reply_to_employee ?? true);

        setCorporateFromEmail(corp?.from_email || "");
        setCorporateFromName(corp?.from_name || "");

        setSmtpHost(smtp?.host || "");
        setSmtpPort(Number(smtp?.port ?? 587));
        setSmtpUser(smtp?.user || "");
        setSmtpPassword(smtp?.password || "");
        setSmtpTls(smtp?.tls ?? true);

        setImapHost(imap?.host || "");
        setImapPort(Number(imap?.port ?? 993));
        setImapUser(imap?.user || "");
        setImapPassword(imap?.password || "");
        setImapSsl(imap?.ssl ?? true);

        const ai = data?.settings_json?.ai || {};
        const aiFeatures = ai?.features || {};
        const openai = ai?.openai || {};

        setAiMode(ai?.mode || "basic");
        setAiFallbackEnabled(ai?.fallback_enabled ?? true);
        setAiLogOutputs(ai?.log_outputs ?? false);

        setAiContactSummaryEnabled(aiFeatures?.contact_summary ?? true);
        setAiEmailWriterEnabled(aiFeatures?.email_writer ?? false);
        setAiLeadScoringEnabled(aiFeatures?.lead_scoring ?? false);
        setAiNextStepEnabled(aiFeatures?.next_step ?? true);

        setAiOpenaiApiKey(openai?.api_key || "");
        setAiOpenaiModel(openai?.model || "gpt-4o-mini");
        setAiOpenaiTemperature(Number(openai?.temperature ?? 0.3));
        setAiOpenaiMaxTokens(Number(openai?.max_tokens ?? 500));

        const google = data?.settings_json?.google_oauth || {};
        const publicRegistration =
          data?.settings_json?.public_registration || {};
        const leads = data?.settings_json?.leads || {};
        const workSchedule = data?.settings_json?.work_schedule || {};
        const filesSettings = data?.settings_json?.files || {};
        const featureVisibility =
          data?.settings_json?.feature_visibility &&
          typeof data.settings_json.feature_visibility === "object"
            ? data.settings_json.feature_visibility
            : {};

        setAcademyEnabled(featureVisibility.academy !== false);
        const storefrontEnabled = data?.settings_json?.storefront?.enabled !== false;
        const shopSettingsVisible = featureVisibility.shop_settings !== false;
        setRetailShopEnabled(storefrontEnabled && shopSettingsVisible);
        const storefrontSettings = data?.settings_json?.storefront || {};
        const rawCommerceModules = storefrontSettings?.commerce_modules;
        const legacySalesMode = String(storefrontSettings?.sales_mode || "shop");
        const resolvedCommerceModules = rawCommerceModules && typeof rawCommerceModules === "object"
          ? {
              shop: rawCommerceModules.shop !== false,
              auction: Boolean(rawCommerceModules.auction),
              marketplace: Boolean(rawCommerceModules.marketplace),
            }
          : {
              shop: legacySalesMode !== "auction",
              auction: legacySalesMode === "auction" || legacySalesMode === "hybrid",
              marketplace: false,
            };
        setRetailShopModules(resolvedCommerceModules);
        setWorkScheduleEnabled(!!workSchedule?.enabled);
        setWorkScheduleMode(workSchedule?.mode === "custom_dates" ? "custom_dates" : "recurring");
        setWorkScheduleSlotGeneration(workSchedule?.slot_generation === "appointment_count" ? "appointment_count" : "duration");
        setWorkScheduleAppointmentCount(Number(workSchedule?.appointment_count ?? 2));
        setWorkScheduleStartTime(String(workSchedule?.start_time || "09:00").slice(0, 5));
        setWorkScheduleEndTime(String(workSchedule?.end_time || "18:00").slice(0, 5));
        setWorkScheduleSlotMinutes(Number(workSchedule?.slot_minutes ?? 15));
        setWorkScheduleMinAheadMinutes(Number(workSchedule?.min_ahead_minutes ?? 15));
        setFilesWorkspaceLayout(
          filesSettings?.workspace_layout === "workflow" ? "workflow" : "gallery"
        );

        setGoogleEnabled(!!google?.enabled);
        setGoogleClientId(google?.client_id || "");
        setGoogleClientSecret(google?.client_secret || "");
        setGoogleRedirectUri(google?.redirect_uri || "");
        setGoogleFrontendSuccessUrl(google?.frontend_success_url || "");
        setGoogleFrontendErrorUrl(google?.frontend_error_url || "");
        setAllowPublicEmployeeRegistration(
          publicRegistration?.allow_employee_registration ?? true
        );
        setVerificationCodeTtlMinutes(
          Number(publicRegistration?.verification_code_ttl_minutes ?? 3)
        );

        setLeadAutoAssign(leads?.lead_auto_assign ?? true);

        baselineRef.current = {
          companyName: data.company_name || "",
          primaryColor: data.primary_color || "#4f46e5",
          loginButtonColor: fromJson || data.login_button_color || "#2575fc",
          defaultLeadSource: data.default_lead_source || "Website",
          defaultDealStage: data.default_deal_stage || "new",
          currency: data.currency || "EUR",
          emailNotifications: !!data.email_notifications,
          taskReminders: !!data.task_reminders,
          logoUrl: data.logo_url || null,
          faviconUrl: data.favicon_url || null,

          emailMode: email?.mode || "hybrid",
          replyToEmployee: email?.reply_to_employee ?? true,
          corporateFromEmail: corp?.from_email || "",
          corporateFromName: corp?.from_name || "",
          smtpHost: smtp?.host || "",
          smtpPort: Number(smtp?.port ?? 587),
          smtpUser: smtp?.user || "",
          smtpPassword: smtp?.password || "",
          smtpTls: smtp?.tls ?? true,
          imapHost: imap?.host || "",
          imapPort: Number(imap?.port ?? 993),
          imapUser: imap?.user || "",
          imapPassword: imap?.password || "",
          imapSsl: imap?.ssl ?? true,

          aiMode: ai?.mode || "basic",
          aiFallbackEnabled: ai?.fallback_enabled ?? true,
          aiLogOutputs: ai?.log_outputs ?? false,
          aiContactSummaryEnabled: aiFeatures?.contact_summary ?? true,
          aiEmailWriterEnabled: aiFeatures?.email_writer ?? false,
          aiLeadScoringEnabled: aiFeatures?.lead_scoring ?? false,
          aiNextStepEnabled: aiFeatures?.next_step ?? true,
          aiOpenaiApiKey: openai?.api_key || "",
          aiOpenaiModel: openai?.model || "gpt-4o-mini",
          aiOpenaiTemperature: Number(openai?.temperature ?? 0.3),
          aiOpenaiMaxTokens: Number(openai?.max_tokens ?? 500),

          googleEnabled: !!google?.enabled,
          googleClientId: google?.client_id || "",
          googleClientSecret: google?.client_secret || "",
          googleRedirectUri: google?.redirect_uri || "",
          googleFrontendSuccessUrl: google?.frontend_success_url || "",
          googleFrontendErrorUrl: google?.frontend_error_url || "",
          allowPublicEmployeeRegistration:
            publicRegistration?.allow_employee_registration ?? true,
          verificationCodeTtlMinutes: Number(
            publicRegistration?.verification_code_ttl_minutes ?? 3
          ),
          leadAutoAssign: leads?.lead_auto_assign ?? true,
          workScheduleEnabled: !!workSchedule?.enabled,
          workScheduleMode: workSchedule?.mode === "custom_dates" ? "custom_dates" : "recurring",
          workScheduleSlotGeneration: workSchedule?.slot_generation === "appointment_count" ? "appointment_count" : "duration",
          workScheduleAppointmentCount: Number(workSchedule?.appointment_count ?? 2),
          workScheduleStartTime: String(workSchedule?.start_time || "09:00").slice(0, 5),
          workScheduleEndTime: String(workSchedule?.end_time || "18:00").slice(0, 5),
          workScheduleSlotMinutes: Number(workSchedule?.slot_minutes ?? 15),
          workScheduleMinAheadMinutes: Number(workSchedule?.min_ahead_minutes ?? 15),
          filesWorkspaceLayout:
            filesSettings?.workspace_layout === "workflow" ? "workflow" : "gallery",
          academyEnabled: featureVisibility.academy !== false,
          retailShopEnabled:
            data?.settings_json?.storefront?.enabled !== false &&
            featureVisibility.shop_settings !== false,
          retailShopModules: resolvedCommerceModules,
        };
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);


  const refreshGoogleDriveStatus = async () => {
    try {
      const data = await crmSettingsApi.getGoogleDriveStatus();
      setGoogleDriveStatus(data);
    } catch (err) {
      console.error("Failed to load Google Drive status", err);
    }
  };

  const connectGoogleDrive = async () => {
    setGoogleDriveLoading(true);
    try {
      const res = await crmSettingsApi.getGoogleDriveAuthUrl();
      if (res?.auth_url) window.open(res.auth_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to start Google Drive connection");
    } finally {
      setGoogleDriveLoading(false);
    }
  };

  const disconnectGoogleDrive = async () => {
    if (!window.confirm(t("google_drive_disconnect_confirm"))) return;
    setGoogleDriveLoading(true);
    try {
      await crmSettingsApi.disconnectGoogleDrive();
      await refreshGoogleDriveStatus();
    } finally {
      setGoogleDriveLoading(false);
    }
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (!file) return;

    const res = await crmSettingsApi.uploadLogo(file);
    setLogoUrl(res.logo_url || null);
    await refreshBranding();
  };

  const removeLogo = async () => {
    await crmSettingsApi.removeLogo();
    setLogoFile(null);
    setLogoUrl(null);
    await refreshBranding();
  };

  const onPickFavicon = async (e) => {
    const file = e.target.files?.[0] || null;
    setFaviconFile(file);
    if (!file) return;

    const res = await crmSettingsApi.uploadFavicon(file);
    setFaviconUrl(res.favicon_url || null);
    await refreshBranding();
  };

  const removeFavicon = async () => {
    await crmSettingsApi.removeFavicon();
    setFaviconFile(null);
    setFaviconUrl(null);
    await refreshBranding();
  };

  const onReset = () => {
    const b = baselineRef.current;
    if (!b) return;

    setCompanyName(b.companyName);
    setPrimaryColor(b.primaryColor);
    setLoginButtonColor(b.loginButtonColor);

    setDefaultLeadSource(b.defaultLeadSource);
    setDefaultDealStage(b.defaultDealStage);
    setCurrency(b.currency);

    setEmailNotifications(b.emailNotifications);
    setTaskReminders(b.taskReminders);

    setLogoFile(null);
    setLogoUrl(b.logoUrl);

    setFaviconFile(null);
    setFaviconUrl(b.faviconUrl);

    setEmailMode(b.emailMode);
    setReplyToEmployee(b.replyToEmployee);

    setCorporateFromEmail(b.corporateFromEmail);
    setCorporateFromName(b.corporateFromName);

    setSmtpHost(b.smtpHost);
    setSmtpPort(b.smtpPort);
    setSmtpUser(b.smtpUser);
    setSmtpPassword(b.smtpPassword);
    setSmtpTls(b.smtpTls);

    setImapHost(b.imapHost);
    setImapPort(b.imapPort);
    setImapUser(b.imapUser);
    setImapPassword(b.imapPassword);
    setImapSsl(b.imapSsl);

    setAiMode(b.aiMode);
    setAiFallbackEnabled(b.aiFallbackEnabled);
    setAiLogOutputs(b.aiLogOutputs);
    setAiContactSummaryEnabled(b.aiContactSummaryEnabled);
    setAiEmailWriterEnabled(b.aiEmailWriterEnabled);
    setAiLeadScoringEnabled(b.aiLeadScoringEnabled);
    setAiNextStepEnabled(b.aiNextStepEnabled);
    setAiOpenaiApiKey(b.aiOpenaiApiKey);
    setAiOpenaiModel(b.aiOpenaiModel);
    setAiOpenaiTemperature(b.aiOpenaiTemperature);
    setAiOpenaiMaxTokens(b.aiOpenaiMaxTokens);

    setGoogleEnabled(b.googleEnabled);
    setGoogleClientId(b.googleClientId);
    setGoogleClientSecret(b.googleClientSecret);
    setGoogleRedirectUri(b.googleRedirectUri);
    setGoogleFrontendSuccessUrl(b.googleFrontendSuccessUrl);
    setGoogleFrontendErrorUrl(b.googleFrontendErrorUrl);
    setAllowPublicEmployeeRegistration(b.allowPublicEmployeeRegistration);
    setVerificationCodeTtlMinutes(b.verificationCodeTtlMinutes);
    setLeadAutoAssign(b.leadAutoAssign);
    setWorkScheduleEnabled(b.workScheduleEnabled ?? false);
    setWorkScheduleMode(b.workScheduleMode || "recurring");
    setWorkScheduleSlotGeneration(b.workScheduleSlotGeneration || "duration");
    setWorkScheduleAppointmentCount(Number(b.workScheduleAppointmentCount ?? 2));
    setWorkScheduleStartTime(b.workScheduleStartTime || "09:00");
    setWorkScheduleEndTime(b.workScheduleEndTime || "18:00");
    setWorkScheduleSlotMinutes(Number(b.workScheduleSlotMinutes ?? 15));
    setWorkScheduleMinAheadMinutes(Number(b.workScheduleMinAheadMinutes ?? 15));
    setFilesWorkspaceLayout(b.filesWorkspaceLayout || "gallery");
    setAcademyEnabled(b.academyEnabled !== false);
    setRetailShopEnabled(b.retailShopEnabled !== false);
    setRetailShopModules(b.retailShopModules || { shop: true, auction: false, marketplace: false });
  };

  const onSaveAll = async () => {
    const safeVerificationCodeTtlMinutes = Math.max(
      1,
      Math.min(60, Number(verificationCodeTtlMinutes || 3))
    );

    const payload = {
      company_name: companyName,
      primary_color: primaryColor,
      login_button_color: loginButtonColor,

      default_lead_source: defaultLeadSource,
      default_deal_stage: defaultDealStage,
      currency,
      email_notifications: emailNotifications,
      task_reminders: taskReminders,

      settings_json: {
        ...(settingsJsonRef.current?.business_preset
          ? { business_preset: settingsJsonRef.current.business_preset }
          : {}),
        ...(settingsJsonRef.current?.industry
          ? { industry: settingsJsonRef.current.industry }
          : {}),
        ...(settingsJsonRef.current?.dashboard
          ? { dashboard: settingsJsonRef.current.dashboard }
          : {}),
        feature_visibility: {
          ...(settingsJsonRef.current?.feature_visibility || {}),
          academy: !!academyEnabled,
          shop_settings: !!retailShopEnabled,
        },

        storefront: {
          ...(settingsJsonRef.current?.storefront || {}),
          enabled: !!retailShopEnabled,
          commerce_modules: {
            shop: Boolean(retailShopModules?.shop),
            auction: Boolean(retailShopModules?.auction),
            marketplace: Boolean(retailShopModules?.marketplace),
          },
          sales_mode: retailShopModules?.auction
            ? (retailShopModules?.shop ? "hybrid" : "auction")
            : "shop",
        },

        branding: {
          login_button_color: loginButtonColor,
        },

        leads: {
          lead_auto_assign: !!leadAutoAssign,
        },

        work_schedule: {
          enabled: !!workScheduleEnabled,
          mode: workScheduleMode,
          slot_generation: workScheduleSlotGeneration,
          appointment_count: Number(workScheduleAppointmentCount || 2),
          start_time: workScheduleStartTime || "09:00",
          end_time: workScheduleEndTime || "18:00",
          slot_minutes: Number(workScheduleSlotMinutes || 15),
          min_ahead_minutes: Number(workScheduleMinAheadMinutes || 15),
        },

        files: {
          workspace_layout:
            filesWorkspaceLayout === "workflow" ? "workflow" : "gallery",
        },

        public_registration: {
          allow_employee_registration: !!allowPublicEmployeeRegistration,
          verification_code_ttl_minutes: safeVerificationCodeTtlMinutes,
        },

        email: {
          mode: emailMode,
          reply_to_employee: !!replyToEmployee,
          corporate: {
            from_email: corporateFromEmail || null,
            from_name: corporateFromName || null,
            smtp: {
              host: smtpHost || null,
              port: Number(smtpPort || 587),
              user: smtpUser || null,
              password: smtpPassword || null,
              tls: !!smtpTls,
            },
            imap: {
              host: imapHost || null,
              port: Number(imapPort || 993),
              user: imapUser || null,
              password: imapPassword || null,
              ssl: !!imapSsl,
            },
          },
        },

        ai: {
          mode: aiMode,
          fallback_enabled: !!aiFallbackEnabled,
          log_outputs: !!aiLogOutputs,
          features: {
            contact_summary: !!aiContactSummaryEnabled,
            email_writer: !!aiEmailWriterEnabled,
            lead_scoring: !!aiLeadScoringEnabled,
            next_step: !!aiNextStepEnabled,
          },
          openai: {
            api_key: aiOpenaiApiKey || null,
            model: aiOpenaiModel || "gpt-4o-mini",
            temperature: Number(aiOpenaiTemperature || 0.3),
            max_tokens: Number(aiOpenaiMaxTokens || 500),
          },
        },

        google_oauth: {
          enabled: !!googleEnabled,
          client_id: googleClientId || null,
          client_secret: googleClientSecret || null,
          redirect_uri: googleRedirectUri || null,
          frontend_success_url: googleFrontendSuccessUrl || null,
          frontend_error_url: googleFrontendErrorUrl || null,
        },
      },
    };

    console.log(
      "[CRMSettingsPage] Saving settings with loginButtonColor:",
      loginButtonColor
    );

    await crmSettingsApi.update(payload);
    settingsJsonRef.current = payload.settings_json || {};
    baselineRef.current = {
      ...(baselineRef.current || {}),
      academyEnabled: !!academyEnabled,
      retailShopEnabled: !!retailShopEnabled,
      retailShopModules: { ...retailShopModules },
    };
    window.dispatchEvent(
      new CustomEvent("velsoft:feature-visibility-changed", {
        detail: {
          feature_visibility: payload.settings_json.feature_visibility,
        },
      }),
    );
    console.log("[CRMSettingsPage] API update complete, calling refreshBranding...");

    await refreshBranding();

    console.log("[CRMSettingsPage] refreshBranding complete");
    alert(t("saved", { defaultValue: "Saved" }));
  };

  const logoForBrowser = previewLogoUrl || logoUrl || defaultLogo;
  const faviconForBrowser = previewFaviconUrl || faviconUrl || null;

  if (loading) {
    return <div className={styles.page}>{t("loading")}...</div>;
  }


  const visibleTabs = TABS.filter((tab) => {
    if (tab.key === "sources") return isAdmin;
    if (tab.key === "pipeline") return isAdmin;
    if (tab.key === "contactCustomFields") return isAdmin;
    if (tab.key === "productCustomFields") return isAdmin;
    return true;
  });

  return (
    <div className={styles.page}>
      <div data-academy="crm-settings-header" className={styles.topBar}>
        <h1 className={styles.h1}>
          {t("crm_settings", { defaultValue: "CRM Settings" })}
        </h1>

        {["modules", "branding", "email", "ai", "google", "workSchedule", "files"].includes(activeTab) && (
          <div data-academy="crm-settings-actions" className={styles.actions}>
            <button className={styles.secondaryAction} onClick={onReset}>
              {t("reset", { defaultValue: "Reset" })}
            </button>
            <button className={styles.primaryAction} onClick={onSaveAll}>
              {t("save_changes", { defaultValue: "Save changes" })}
            </button>
          </div>
        )}
      </div>

      <div data-academy="crm-settings-tabs">
        <ResponsiveSettingsTabs
          tabs={visibleTabs.map((tab) => ({
            ...tab,
            label:
              tab.key === "rateLimit"
                ? "Rate Limiting"
                : t(tab.labelKey) || tab.key,
          }))}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("crm_settings", { defaultValue: "CRM Settings" })}
          dataAcademyPrefix="crm-settings"
        />
      </div>

      <div data-academy="crm-settings-panel" className={styles.panel}>
        {activeTab === "modules" && (
          <ModulesTab
            academyEnabled={academyEnabled}
            setAcademyEnabled={setAcademyEnabled}
            retailShopEnabled={retailShopEnabled}
            setRetailShopEnabled={setRetailShopEnabled}
            retailShopModules={retailShopModules}
            setRetailShopModules={setRetailShopModules}
          />
        )}

        {activeTab === "branding" && (
          <BrandingTab
            companyName={companyName}
            setCompanyName={setCompanyName}
            primaryColor={primaryColor}
            setPrimaryColor={setPrimaryColor}
            loginButtonColor={loginButtonColor}
            setLoginButtonColor={setLoginButtonColor}
            allowPublicEmployeeRegistration={allowPublicEmployeeRegistration}
            setAllowPublicEmployeeRegistration={setAllowPublicEmployeeRegistration}
            verificationCodeTtlMinutes={verificationCodeTtlMinutes}
            setVerificationCodeTtlMinutes={setVerificationCodeTtlMinutes}
            logoUrl={logoUrl}
            previewLogoUrl={previewLogoUrl}
            faviconUrl={faviconUrl}
            previewFaviconUrl={previewFaviconUrl}
            onPickLogo={onPickLogo}
            removeLogo={removeLogo}
            onPickFavicon={onPickFavicon}
            removeFavicon={removeFavicon}
            logoForBrowser={logoForBrowser}
            faviconForBrowser={faviconForBrowser}
          />
        )}

        {activeTab === "email" && (
          <EmailTab
            emailMode={emailMode}
            setEmailMode={setEmailMode}
            replyToEmployee={replyToEmployee}
            setReplyToEmployee={setReplyToEmployee}
            corporateFromEmail={corporateFromEmail}
            setCorporateFromEmail={setCorporateFromEmail}
            corporateFromName={corporateFromName}
            setCorporateFromName={setCorporateFromName}
            smtpHost={smtpHost}
            setSmtpHost={setSmtpHost}
            smtpPort={smtpPort}
            setSmtpPort={setSmtpPort}
            smtpUser={smtpUser}
            setSmtpUser={setSmtpUser}
            smtpPassword={smtpPassword}
            setSmtpPassword={setSmtpPassword}
            smtpTls={smtpTls}
            setSmtpTls={setSmtpTls}
            imapHost={imapHost}
            setImapHost={setImapHost}
            imapPort={imapPort}
            setImapPort={setImapPort}
            imapUser={imapUser}
            setImapUser={setImapUser}
            imapPassword={imapPassword}
            setImapPassword={setImapPassword}
            imapSsl={imapSsl}
            setImapSsl={setImapSsl}
            emailInnerTab={emailInnerTab}
            setEmailInnerTab={setEmailInnerTab}
          />
        )}

        {activeTab === "ai" && (
          <AITab
            aiMode={aiMode}
            setAiMode={setAiMode}
            aiFallbackEnabled={aiFallbackEnabled}
            setAiFallbackEnabled={setAiFallbackEnabled}
            aiLogOutputs={aiLogOutputs}
            setAiLogOutputs={setAiLogOutputs}
            aiContactSummaryEnabled={aiContactSummaryEnabled}
            setAiContactSummaryEnabled={setAiContactSummaryEnabled}
            aiEmailWriterEnabled={aiEmailWriterEnabled}
            setAiEmailWriterEnabled={setAiEmailWriterEnabled}
            aiLeadScoringEnabled={aiLeadScoringEnabled}
            setAiLeadScoringEnabled={setAiLeadScoringEnabled}
            aiNextStepEnabled={aiNextStepEnabled}
            setAiNextStepEnabled={setAiNextStepEnabled}
            aiOpenaiApiKey={aiOpenaiApiKey}
            setAiOpenaiApiKey={setAiOpenaiApiKey}
            aiOpenaiModel={aiOpenaiModel}
            setAiOpenaiModel={setAiOpenaiModel}
            aiOpenaiTemperature={aiOpenaiTemperature}
            setAiOpenaiTemperature={setAiOpenaiTemperature}
            aiOpenaiMaxTokens={aiOpenaiMaxTokens}
            setAiOpenaiMaxTokens={setAiOpenaiMaxTokens}
          />
        )}

        {activeTab === "google" && (
          <GoogleTab
            googleEnabled={googleEnabled}
            setGoogleEnabled={setGoogleEnabled}
            googleClientId={googleClientId}
            setGoogleClientId={setGoogleClientId}
            googleClientSecret={googleClientSecret}
            setGoogleClientSecret={setGoogleClientSecret}
            googleRedirectUri={googleRedirectUri}
            setGoogleRedirectUri={setGoogleRedirectUri}
            googleFrontendSuccessUrl={googleFrontendSuccessUrl}
            setGoogleFrontendSuccessUrl={setGoogleFrontendSuccessUrl}
            googleFrontendErrorUrl={googleFrontendErrorUrl}
            setGoogleFrontendErrorUrl={setGoogleFrontendErrorUrl}
            googleDriveStatus={googleDriveStatus}
            googleDriveLoading={googleDriveLoading}
            onConnectGoogleDrive={connectGoogleDrive}
            onRefreshGoogleDriveStatus={refreshGoogleDriveStatus}
            onDisconnectGoogleDrive={disconnectGoogleDrive}
          />
        )}

        {activeTab === "workSchedule" && (
          <WorkScheduleTab
            enabled={workScheduleEnabled}
            setEnabled={setWorkScheduleEnabled}
            startTime={workScheduleStartTime}
            setStartTime={setWorkScheduleStartTime}
            endTime={workScheduleEndTime}
            setEndTime={setWorkScheduleEndTime}
            slotMinutes={workScheduleSlotMinutes}
            setSlotMinutes={setWorkScheduleSlotMinutes}
            minAheadMinutes={workScheduleMinAheadMinutes}
            setMinAheadMinutes={setWorkScheduleMinAheadMinutes}
            mode={workScheduleMode}
            setMode={setWorkScheduleMode}
            slotGeneration={workScheduleSlotGeneration}
            setSlotGeneration={setWorkScheduleSlotGeneration}
            appointmentCount={workScheduleAppointmentCount}
            setAppointmentCount={setWorkScheduleAppointmentCount}
          />
        )}

        {activeTab === "files" && (
          <section className={styles.filesSettingsPanel}>
            <div className={styles.filesSettingsHeader}>
              <div>
                <span className={styles.filesSettingsEyebrow}>
                  {t("crm_files_settings_eyebrow", {
                    defaultValue: "Files & photos",
                  })}
                </span>
                <h2>
                  {t("crm_files_settings_title", {
                    defaultValue: "Отображение фотографий",
                  })}
                </h2>
                <p>
                  {t("crm_files_settings_hint", {
                    defaultValue:
                      "Выбранный стиль используется одинаково в задачах, сделках и рабочей области контакта.",
                  })}
                </p>
              </div>

              <span className={styles.filesSettingsSavedHint}>
                {t("crm_files_settings_storage_hint", {
                  defaultValue:
                    "Меняется только интерфейс. Google Drive и хранение файлов остаются прежними.",
                })}
              </span>
            </div>

            <div
              className={styles.filesLayoutGrid}
              role="radiogroup"
              aria-label={t("crm_files_layout_label", {
                defaultValue: "Стиль отображения фотографий",
              })}
            >
              <button
                type="button"
                role="radio"
                aria-checked={filesWorkspaceLayout === "gallery"}
                className={`${styles.filesLayoutCard} ${
                  filesWorkspaceLayout === "gallery"
                    ? styles.filesLayoutCardActive
                    : ""
                }`}
                onClick={() => setFilesWorkspaceLayout("gallery")}
              >
                <div className={styles.filesLayoutPreviewGallery} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className={styles.filesLayoutCardBody}>
                  <span className={styles.filesLayoutRadio}>
                    {filesWorkspaceLayout === "gallery" ? "✓" : ""}
                  </span>
                  <div>
                    <strong>
                      {t("crm_files_layout_gallery", {
                        defaultValue: "Универсальная галерея",
                      })}
                    </strong>
                    <p>
                      {t("crm_files_layout_gallery_hint", {
                        defaultValue:
                          "Один компактный список фотографий. Подходит для большинства компаний.",
                      })}
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={filesWorkspaceLayout === "workflow"}
                className={`${styles.filesLayoutCard} ${
                  filesWorkspaceLayout === "workflow"
                    ? styles.filesLayoutCardActive
                    : ""
                }`}
                onClick={() => setFilesWorkspaceLayout("workflow")}
              >
                <div className={styles.filesLayoutPreviewWorkflow} aria-hidden="true">
                  <div><b>{t("before")}</b><span /></div>
                  <div><b>{t("during_work")}</b><span /></div>
                  <div><b>{t("after")}</b><span /></div>
                </div>
                <div className={styles.filesLayoutCardBody}>
                  <span className={styles.filesLayoutRadio}>
                    {filesWorkspaceLayout === "workflow" ? "✓" : ""}
                  </span>
                  <div>
                    <strong>
                      {t("crm_files_layout_workflow", {
                        defaultValue: "По этапам работы",
                      })}
                    </strong>
                    <p>
                      {t("crm_files_layout_workflow_hint", {
                        defaultValue:
                          "До работы, в процессе и после работы. Подходит сервисам, ремонту и строительству.",
                      })}
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className={styles.filesSettingsFooter}>
              <div>
                <strong>
                  {filesWorkspaceLayout === "workflow"
                    ? t("crm_files_current_workflow", {
                        defaultValue: "Выбран режим «По этапам работы»",
                      })
                    : t("crm_files_current_gallery", {
                        defaultValue: "Выбрана универсальная галерея",
                      })}
                </strong>
                <span>
                  {t("crm_files_save_hint", {
                    defaultValue:
                      "Нажмите «Сохранить» в верхней части страницы, чтобы применить настройку для tenant.",
                  })}
                </span>
              </div>
            </div>
          </section>
        )}

        {activeTab === "rateLimit" && (
          <RateLimitTab isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
        )}

        {activeTab === "sources" && isAdmin && <SourcesPage />}

        {activeTab === "pipeline" && isAdmin && <PipelinePage />}

        {activeTab === "contactCustomFields" && isAdmin && (
          <ContactCustomFieldsTab />
        )}

        {activeTab === "productCustomFields" && isAdmin && (
          <ProductCustomFieldsTab />
        )}

        {activeTab === "externalApi" && <ExternalApiTab />}

        {activeTab !== "branding" &&
          activeTab !== "email" &&
          activeTab !== "ai" &&
          activeTab !== "google" &&
          activeTab !== "rateLimit" &&
          activeTab !== "workSchedule" &&
          activeTab !== "sources" &&
          activeTab !== "pipeline" &&
          activeTab !== "contactCustomFields" &&
          activeTab !== "productCustomFields" &&
          activeTab !== "externalApi" && (
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                {activeTab === "leads"
                  ? t("crm_settings_leads_title", {
                      defaultValue: "Leads",
                    })
                  : activeTab === "deals"
                  ? t("crm_settings_deals_title", {
                      defaultValue: "Deals",
                    })
                  : t("crm_settings_notifications_title", {
                      defaultValue: "Notifications",
                    })}
              </div>
              <div className={styles.cardSub}>
                {t("crm_settings_section_unchanged", {
                  defaultValue: "This section is unchanged for now.",
                })}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
